import React, { useState } from 'react';
import { Box, Typography, Avatar, Divider, IconButton } from '@mui/material';
import ReplyIcon from '@mui/icons-material/Reply';
import RatingCommentForm from './ratingsCommentForum';
import { CommentType } from '../../models/commentType';
import Rating from '@mui/material/Rating';


interface CommentsSectionProps {
  comments: CommentType[];
  /** Called when a new top-level comment or reply is submitted */
  onSubmitComment: (data: { parentId?: string; text: string, rating: number }) => void;
  onFlagInappropriate?: (commentId: string, parentId?: string) => void;
  hideRatingComment: boolean;
}

const CommentItem: React.FC<{
  comment: CommentType;
  depth?: number;
  onReplyInitiate: (id: string) => void;
  replyingTo: string | null;
  onSubmitReply: (parentId: string, text: string, rating: number) => void;
  onFlagInappropriate?: (commentId: string, parentId?: string) => void;
}> = ({ comment, depth = 0, onReplyInitiate, replyingTo, onSubmitReply, onFlagInappropriate }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', ml: depth }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mr: 1, color: '#162C1F' }}>
            {comment.author}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {new Date(comment.date).toLocaleDateString()}
          </Typography>
          <IconButton
            size="small"
            onClick={() => onReplyInitiate(comment.id)}
            sx={{ ml: 'auto' }}
            aria-label="reply"
          >
            <ReplyIcon fontSize="small" />
          </IconButton>


          {/* Flag button (conditionally shown) */}
          {onFlagInappropriate && (
            <IconButton
              size="small"
              onClick={() => onFlagInappropriate(comment.id, replyingTo ? replyingTo : undefined)}
              aria-label="flag"
            >
              🚩
            </IconButton>
          )}
        </Box>

        {/* Top-level rating only */}
        {depth === 0 && (
          <Box sx={{ mt: 0.5 }}>
            <Rating value={comment.rating ?? 0} readOnly size="small" />
          </Box>
        )}

        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
          {comment.text}
        </Typography>
      </Box>
    </Box>

    {/* Reply form for this comment */}
    {replyingTo === comment.id && (
      <Box sx={{ mt: 2 }}>
        <RatingCommentForm
          onSubmit={({ rating, comment: text }) => onSubmitReply(comment.id, text, rating)}
          initialRating={0}
          initialComment=""
          isReply={true}
        />
      </Box>
    )}

    {/* Render replies recursively */}
    {comment.replies && comment.replies.length > 0 && (
      <Box sx={{ mt: 2 }}>
        {comment.replies.map(reply => (
          <CommentItem
            key={reply.id}
            comment={reply}
            depth={depth + 1}
            onReplyInitiate={onReplyInitiate}
            replyingTo={replyingTo}
            onSubmitReply={onSubmitReply}
            onFlagInappropriate={onFlagInappropriate}
          />
        ))}
      </Box>
    )}
  </Box>
);


const CommentsSection: React.FC<CommentsSectionProps> = ({ comments, onSubmitComment, onFlagInappropriate, hideRatingComment }) => {
  const [replyingTo, setReplyingTo] = useState<string | null>(null);

  const handleReplyInitiate = (commentId: string) => {
    setReplyingTo(prev => (prev === commentId ? null : commentId));
  };

  const handleSubmitReply = (parentId: string, text: string, rating: number) => {
    onSubmitComment({ parentId, text, rating });
    setReplyingTo(null);
  };

  return (
    <Box sx={{ backgroundColor: '#F5F5EC' }}>
      <Typography variant="h6" sx={{ mb: 2, color: '#162C1F' }}>
        Customer Comments
      </Typography>

      {/* Top-level new comment form */}
      {!hideRatingComment &&
        <Box sx={{ mb: 3 }}>
          <RatingCommentForm
            onSubmit={({ comment: text, rating }) => onSubmitComment({ text, rating })}
            initialRating={0}
            initialComment=""
            isReply={false}
          />
        </Box>
      }

      {comments.length === 0 ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <Typography variant="h6" color="#162C1F">
            No comments yet.
          </Typography>
        </Box>
      ) : (
        comments.map(comment => (
          <React.Fragment key={comment.id}>
            <CommentItem
              comment={comment}
              onReplyInitiate={handleReplyInitiate}
              replyingTo={replyingTo}
              onSubmitReply={handleSubmitReply}
              onFlagInappropriate={onFlagInappropriate}
            />
            {/** green divider */}
            <Divider sx={{ my: 2, borderColor: '#162C1F' }} />
          </React.Fragment>
        ))
      )}
    </Box>
  );
};


export default CommentsSection;
